import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import { ImpressionTracking } from './ImpressionTracking';
import type { Company, FeedItem } from './types';
import type { HeadingElements } from '@/utilities/heading';

type Props = {
  company: Company;
  feedItem: FeedItem;
  index: number;
  titleElement?: HeadingElements;
  onIntersection?: (id: string) => void;
};

export const NarrativeFeedCard = ({
  company,
  feedItem,
  index,
  titleElement: Title = 'h4',
  onIntersection,
}: Props) => {
  const { companyId } = company;
  const {
    activityId,
    authorImageUrl,
    dateFormatted,
    id,
    subType,
    text,
    title,
    type,
    url,
  } = feedItem;
  const cachedAuthorImageUrl = useCloudflareImage({
    src:
      authorImageUrl ??
      'https://media.simplywall.st/news/1695690646346-fallback-author-image.png',
    width: 60,
    height: 60,
    fit: 'cover',
  });
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'feed',
  });

  const trackClick = useCallback(() => {
    const eventId = id;
    trackEvent({
      action: 'click',
      modifier: 'card-item',
      index: index,
      eventType: type,
      eventSubtype: subType,
      eventId,
      activityId,
      companyId,
    });
  }, [trackEvent, index, type, subType, id, activityId, companyId]);

  const companyCanonicalUrl = company.canonicalUrl.split('/');

  const slug = [
    '',
    'community',
    'narratives',
    ...companyCanonicalUrl.slice(2),
    url.split('/').pop(),
  ].join('/');

  return (
    <Track>
      <RouterLink
        noUnderline
        to={slug}
        onClick={() => {
          trackClick();
        }}
      >
        <ImpressionTracking id={activityId} onIntersection={onIntersection}>
          <article className="grid cursor-pointer grid-cols-[min-content_auto] gap-x1_5 rounded-x1 p-x1 hover:bg-surface-2">
            <img
              className="border size-x5 max-h-x5 min-h-x5 min-w-x5 max-w-x5 rounded-full border-solid border-strong object-cover"
              src={cachedAuthorImageUrl}
              alt="author-image"
            />
            <div className="grid gap-x0_5">
              {title && (
                <Title className="m-x0 line-clamp-3 text-xs font-normal text-solid">
                  {title}
                </Title>
              )}
              <time className="text-tiny text-softer">{dateFormatted}</time>
              <span className="line-clamp-2 text-tiny text-softer">{text}</span>
            </div>
          </article>
        </ImpressionTracking>
      </RouterLink>
    </Track>
  );
};
